<script setup>
import { computed, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import { useHead } from '@unhead/vue';
import { usePlatform, useUserPlatform } from '/app/composables/platform';
import { useAppInit } from '/app/composables/app-init';
import { useBus } from '/app/composables/bus';
import { Toaster } from 'vue-sonner';
import kebabCase from 'lodash/kebabCase';
import { config } from '/app/config';

import AppProvider from '/app/components/providers/AppProvider.vue';
import AppUpdate from '/app/components/AppUpdate.vue';
import NNotifier from '/app/components/NNotifier.vue';

// partials
import AppHeader from '/app/layouts/partials/Header.vue';
import AppFooter from '/app/layouts/partials/Footer.vue';

// layouts
import Wide from '/app/layouts/wide.vue';
import Boxed from '/app/layouts/boxed.vue';
import Auth from '/app/layouts/auth.vue';
import Raw from '/app/layouts/raw.vue';
import BoxedWithoutGameDash from '/app/layouts/boxed-without-game-dash.vue';
import NIcon from '/app/components/NIcon.vue';
import NSharingPopup from '/app/components/sharing/Popup.vue';
import NNavbarNotification from '/app/components/NNavbarNotification.vue';
import PlatformSuspended from '/app/components/PlatformSuspended.vue';

const showProductionAlert =
    config.app.showProductionAlert ||
    (!config.app.isProd &&
        config.api.baseURL.endsWith('/api'));

const prefix =
    !config.app.isProd
        ? showProductionAlert
            ? '[prod]'
            : '[dev]'
        : null;

// ── Composables ─────────────────────────────────────────────────────

const route = useRoute();
const { title, slogan, isUserPlatform, rootPlatform } = usePlatform();
const { isSuspended } = useUserPlatform();
const { on } = useBus();

const sharingPopup = useTemplateRef('sharingPopup');

// ── App Initialization ──────────────────────────────────────────────

useAppInit();

on('sharing:popup', (payload) => {
    sharingPopup.value?.open(payload);
});

// ── Head ────────────────────────────────────────────────────────────

const titled = computed(() => {
    if (isUserPlatform.value) {
        return [rootPlatform.value?.title, title.value].filter(Boolean).join(' · ');
    }
    return title.value;
});

useHead({
    title: titled.value,
    titleTemplate: (t) => {
        return [prefix, ...(t === titled.value ? [t, slogan.value] : [t, titled.value])].filter(Boolean).join(' · ');
    },
});

// ── Layout ──────────────────────────────────────────────────────────

const LAYOUTS = { Auth, Wide, Boxed, Raw, BoxedWithoutGameDash };
const LAYOUT_MAP = {
    wide: 'Wide',
    boxed: 'Boxed',
    auth: 'Auth',
    raw: 'Raw',
    boxedwithoutgamedash: 'BoxedWithoutGameDash',
};

const layout = computed(() => (route.meta.layout || 'wide').toLowerCase());
const classes = computed(() => [kebabCase(`page.${route.name}`), layout.value]);
const layoutComponent = computed(() => LAYOUTS[LAYOUT_MAP[layout.value]] || Wide);
</script>

<template>
    <AppProvider>
        <NNavbarNotification :immediate="showProductionAlert">
            <div class="bg-red-500 text-white py-4 z-[800]">
                <div class="container mx-auto flex items-center leading-none">
                    <NIcon as="exclamation-circle-solid" class="mr-2 pb-1" />
                    Be careful, you are accessing the platform connected to the production
                    database.
                </div>
            </div>
        </NNavbarNotification>

        <AppHeader />

        <main class="app-main" :class="classes">
            <PlatformSuspended v-if="isUserPlatform && isSuspended" />
            <template v-else>
                <Component :is="layoutComponent" />
                
                <NSharingPopup ref="sharingPopup" />
            </template>

        </main>

        <AppFooter v-if="!$route.meta.hideFooter" />
        <Toaster position="bottom-right" rich-colors />
        <AppUpdate />
        <NNotifier />
    </AppProvider>
</template>
